<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { Head, router } from '@inertiajs/vue3';
import DateTimePicker from '@/Components/DateTimePicker.vue';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
</script>

<script>
export default {

    props: {
        errors: {
            type: Object
        },
        result: {
            type: Object
        },
        user: {
            type: Object
        }
    },

    data() {
        return {
            ajaxRequest: false,
            ticketValid: false,
            details: {
                address: '',
                name: '',
                phone: '',
                email: '',
            },
            ticketData: {
                ticketNr: '', // 'AS-2896',
                flightNr: '',
                flightDate: '',
                flightTime: '',
                name: '',
                email: '',
                phone: '+'
            }
        }
    },
    methods: {

        checkTicket() {

            router.post(route('returnCheck'), {
                ticketNr: this.ticketData.ticketNr
            }, {
                preserveState: true,
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                only: ['errors', 'result'],
                onStart: visit => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {
                    if (page.props?.result) {
                        this.details = page.props.result;

                        if (this.details.name) {
                            this.ticketData.name = this.details.name;
                        }
                        if (this.details.phone) {
                            this.ticketData.phone = this.details.phone;
                        }
                        if (this.details.email) {
                            this.ticketData.email = this.details.email;
                        }

                        this.ticketValid = true;
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                    this.ticketValid = false;
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        },

        saveTicket() {

            router.post(route('returnSave'), this.ticketData, {
                preserveState: true,
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                only: ['errors', 'saveSuccess'],
                onStart: visit => {
                    this.ajaxRequest = true;
                },
                onSuccess: page => {

                    // Reset ticket details
                    this.ticketData.ticketNr = '';
                    this.ticketData.flightDate = '';
                    this.ticketData.flightTime = '';
                    this.ticketData.name = '';
                    this.ticketData.email = '';
                    this.ticketData.phone = '';

                    this.ticketValid = false;
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        }

    },
    mounted() {

        if (this.user) {
            this.ticketData.name = this.user.name;
            this.ticketData.email = this.user.email;
            this.ticketData.phone = this.user.phone;
        }

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'return',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
}
</script>

<template>

    <Head :title="$t('Retúr jegyérvényesítés')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container-fluid">
                    <div class="container">
                        <div class="row align-items-center justify-content-center">
                            <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                                <h1 class="header-h1">{{ $t('Retúr jegy érvényesítése') }}</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <div class="header-placeholder"></div>

        <div class="subpage-content">
            <div class="container subpage-container">
                <div class="row">
                    <main class="main col-12">
                        <section>
                            <div class="row section-title d-none">
                                <div class="col-12">
                                    <h2>{{ $t('Retúr jegyérvényesítés') }}</h2>
                                </div>
                            </div>
                            <div class="row" v-if="!ticketValid">
                                <div class="col-12">
                                    <p class="fs-6 fw-bold">
                                        {{ $t('Ha a repülőtéren retúrjegyet vásárolt, itt lefoglalhatja a visszaútját. Kérjük, készítse elő a miniBUD jegyszámát.') }}
                                    </p>
                                </div>
                                <div class="col-md-4 offset-md-2">
                                    <div class="form-floating mb-3">
                                        <input type="text" class="form-control text-uppercase" :class="{ 'is-invalid': errors.ticketNr }" id="ticketNr" :placeholder="$t('miniBUD retúr jegyszám')" maxlength="100" v-model="ticketData.ticketNr">
                                        <label for="ticketNr">{{ $t('miniBUD retúr jegyszám') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.ticketNr">{{ errors.ticketNr }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="mb-3">
                                        <button type="button" class="btn btn-primary text-uppercase" @click="checkTicket" :disabled="ajaxRequest">{{ $t('Jegy ellenőrzése') }}</button>
                                    </div>
                                </div>
                                <div class="col-12">
                                    <p class="fw-bold"><i class="fas fa-info-circle"></i>
                                        {{ $t('A jegyszámot a retúr jegyen találja, az odaút során adtuk Önnek. Pl. AS-111111') }}</p>
                                </div>
                            </div>
                            <div class="row" v-else>
                                <div class="col-12">
                                    <h6 class="fw-bold mb-3">{{ $t('Az Ön foglalási adatai') }}:</h6>
                                    <dl class="row">
                                        <dt class="col-sm-3 text-end">{{ $t('Hotel vagy cím') }}</dt>
                                        <dd class="col-sm-9">{{ details.address }}</dd>
                                        <dt class="col-sm-3 text-end">{{ $t('Név') }}</dt>
                                        <dd class="col-sm-9">{{ details.name || '-' }}</dd>
                                        <dt class="col-sm-3 text-end">{{ $t('Tel. szám') }}</dt>
                                        <dd class="col-sm-9">{{ details.phone || '-' }}</dd>
                                        <dt class="col-sm-3 text-end">{{ $t('E-mail') }}</dt>
                                        <dd class="col-sm-9">{{ details.email || '-' }}</dd>
                                    </dl>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <input type="text" class="form-control text-uppercase" :class="{ 'is-invalid': errors.flightNr }" id="flightNr" :placeholder="$t('Induló repülő járatszáma')" maxlength="10" v-model="ticketData.flightNr">
                                        <label for="flightNr">{{ $t('Induló repülő járatszáma') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.flightNr">{{ errors.flightNr }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <DateTimePicker id="flightDate" :placeholder="$t('Indulás dátuma')" class="form-control" :class="{ 'is-invalid': errors.flightDate }" :min-date="new Date()" picker-type="calendar" picker-format="L" :required="true" v-model="ticketData.flightDate" />
                                        <div class="invalid-feedback" v-if="errors.flightDate">{{ errors.flightDate }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <DateTimePicker id="flightTime" :placeholder="$t('Indulás időpontja')" class="form-control" :class="{ 'is-invalid': errors.flightTime }" picker-type="clock" picker-format="HH:mm" :required="true" v-model="ticketData.flightTime" />
                                        <div class="invalid-feedback" v-if="errors.flightTime">{{ errors.flightTime }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('Név')" v-model="ticketData.name">
                                        <label for="name">{{ $t('Név') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" v-model="ticketData.email">
                                        <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-floating mb-3">
                                        <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="$t('Mobiltelefonszám')" v-model="ticketData.phone" />
                                        <label for="phone">{{ $t('Mobiltelefonszám') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                    </div>
                                </div>
                                <div class="col-md-4 offset-md-8 text-end">
                                    <button type="button" class="btn btn-primary px-5 text-uppercase" @click="saveTicket" :disabled="ajaxRequest">{{ $t('Mentés') }}</button>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
